import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import ForecastWidget from './ForecastWidget';
import { Sparkles, Loader } from 'lucide-react';

const getHealthGradient = (score) => {
  if (score >= 70) return 'from-[#22c55e]/20 to-[#22c55e]/5';
  if (score >= 40) return 'from-yellow-500/20 to-yellow-500/5';
  return 'from-red-500/20 to-red-500/5';
};

const getHealthRing = (score) => {
  if (score >= 70) return 'ring-[#22c55e]/30';
  if (score >= 40) return 'ring-yellow-500/30';
  return 'ring-red-500/30';
};

// Skeleton Loader
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-zinc-800/60 rounded-xl ${className}`} />
);

const HealthOverview = ({ addToast }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(null);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [narratives, setNarratives] = useState({});
  const [fetchingNarrative, setFetchingNarrative] = useState({});

  useEffect(() => {
    fetchHealthData();
  }, []);

  const fetchHealthData = async () => {
    try {
      const response = await fetch(`${API_URL}/health-overview`);
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const runAnalysis = async (accountId) => {
    setAnalyzing(accountId);
    try {
      await fetch(`${API_URL}/accounts/${accountId}/analyze`, { method: 'POST' });
      addToast('AI Analysis complete', 'success');
      await fetchHealthData();
    } catch (error) {
      addToast('Analysis failed', 'error');
    }
    setAnalyzing(null);
  };

  const fetchNarrative = async (accountId) => {
    if (narratives[accountId] || fetchingNarrative[accountId]) return;
    setFetchingNarrative(prev => ({ ...prev, [accountId]: true }));
    try {
      const res = await fetch(`${API_URL}/ai/account-narrative?account_id=${accountId}`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        setNarratives(prev => ({ ...prev, [accountId]: d }));
      }
    } catch (e) {
      console.error('Narrative error:', e);
    }
    setFetchingNarrative(prev => ({ ...prev, [accountId]: false }));
  };

  const HealthCard = ({ account, status }) => {
    const isExpanded = expandedAccount === account.id;
    
    return (
      <div 
        className={`bg-gradient-to-br ${getHealthGradient(account.health_score)} rounded-2xl border transition-colors cursor-pointer ${
          status === 'healthy' ? 'border-[#22c55e]/20 hover:border-[#22c55e]/40' :
          status === 'warning' ? 'border-yellow-500/20 hover:border-yellow-500/40' :
          'border-red-500/20 hover:border-red-500/40'
        } ${isExpanded ? 'ring-2 ' + getHealthRing(account.health_score) : ''}`}
        onClick={() => setExpandedAccount(isExpanded ? null : account.id)}
      >
        <div className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              {/* Platform Icon */}
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shadow-lg ${
                account.platform === 'META' ? 'bg-[#1877F2] shadow-[#1877F2]/20' : 'bg-white shadow-white/10'
              }`}>
                {account.platform === 'META' ? (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                ) : (
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                    <path d="M23.766 12.2764C23.766 11.4607 23.6999 10.6406 23.5588 9.83807H12.24V14.4591H18.7217C18.4528 15.9494 17.5885 17.2678 16.323 18.1056V21.1039H20.19C22.4608 19.0139 23.766 15.9274 23.766 12.2764Z" fill="#4285F4" />
                    <path d="M12.2401 24.0008C15.4766 24.0008 18.2059 22.9382 20.1945 21.1039L16.3275 18.1055C15.2517 18.8375 13.8627 19.252 12.2445 19.252C9.11388 19.252 6.45946 17.1399 5.50705 14.3003H1.5166V17.3912C3.55371 21.4434 7.7029 24.0008 12.2401 24.0008Z" fill="#34A853" />
                    <path d="M5.50253 14.3003C5.00236 12.8099 5.00236 11.1961 5.50253 9.70575V6.61481H1.51649C-0.18551 10.0056 -0.18551 14.0004 1.51649 17.3912L5.50253 14.3003Z" fill="#FBBC05" />
                    <path d="M12.2401 4.74966C13.9509 4.7232 15.6044 5.36697 16.8434 6.54867L20.2695 3.12262C18.1001 1.0855 15.2208 -0.0344664 12.2401 0.000808666C7.7029 0.000808666 3.55371 2.55822 1.5166 6.61481L5.50264 9.70575C6.45064 6.86173 9.10947 4.74966 12.2401 4.74966Z" fill="#EA4335" />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="font-bold text-white text-base">{account.name}</h3>
                <p className="text-xs text-zinc-500 font-mono flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    account.platform === 'META' ? 'bg-[#1877F2]' : 'bg-white'
                  }`}></span>
                  {account.campaign_count} campaigns
                </p>
              </div>
            </div>
            
            {/* Health Score Circle */}
            <div className="relative">
              <svg className="w-16 h-16 -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-white/5"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${account.health_score * 1.76} 176`}
                  strokeLinecap="round"
                  className={`${
                    account.health_score >= 70 ? 'text-[#22c55e]' :
                    account.health_score >= 40 ? 'text-yellow-400' : 'text-red-400'
                  }`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-lg font-bold font-mono ${
                  account.health_score >= 70 ? 'text-[#22c55e]' :
                  account.health_score >= 40 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {Math.round(account.health_score)}%
                </span>
              </div>
            </div>
          </div>

          {/* Warning Badges */}
          {account.critical_campaigns > 0 && (
            <div className="bg-red-500/10 text-red-400 px-3 py-2 rounded-xl text-xs font-medium border border-red-500/20 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {account.critical_campaigns} critical campaign{account.critical_campaigns > 1 ? 's' : ''} need attention
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && account.ai_analysis && (
            <div className="mt-4 pt-4 border-t border-white/10 animate-fade-in">
              <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                AI Analysis
              </h4>
              <p className="text-sm text-zinc-300 leading-relaxed">{account.ai_analysis}</p>
            </div>
          )}

          {/* AI Narrative */}
          {narratives[account.id] && (
            <div className="mt-3 p-3 bg-black/30 rounded-xl border border-white/10 space-y-2.5">
              <div className="flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-zinc-300 leading-relaxed">{narratives[account.id].narrative}</p>
              </div>
              {narratives[account.id].actions?.length > 0 && (
                <div className="pl-5 space-y-1.5">
                  {narratives[account.id].actions.map((action, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-[10px] font-bold text-[#22c55e]/60 font-mono mt-0.5 w-3 flex-shrink-0">{i + 1}.</span>
                      <p className="text-[11px] text-zinc-300 leading-snug">{action}</p>
                    </div>
                  ))}
                </div>
              )}
              {narratives[account.id].top_action && (
                <div className="pl-5 pt-1 border-t border-white/5">
                  <p className="text-[10px] text-[#22c55e] font-semibold tracking-wide">↳ {narratives[account.id].top_action}</p>
                </div>
              )}
            </div>
          )}

          {/* Forecast Widget */}
          <ForecastWidget accountId={account.id} metric="cpl" />

          {/* Action Buttons Row */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={(e) => { e.stopPropagation(); fetchNarrative(account.id); }}
              disabled={fetchingNarrative[account.id] || !!narratives[account.id]}
              className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                narratives[account.id]
                  ? 'bg-[#22c55e]/10 text-[#22c55e] border border-[#22c55e]/20 cursor-default'
                  : fetchingNarrative[account.id]
                  ? 'bg-white/5 text-zinc-500 cursor-not-allowed border border-white/5'
                  : 'bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20 border border-[#22c55e]/20'
              }`}
            >
              {fetchingNarrative[account.id] ? (
                <><Loader className="w-3.5 h-3.5 animate-spin" /><span>Generating…</span></>
              ) : narratives[account.id] ? (
                <><Sparkles className="w-3.5 h-3.5" /><span>Insight Ready</span></>
              ) : (
                <><Sparkles className="w-3.5 h-3.5" /><span>Get AI Insight</span></>
              )}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); runAnalysis(account.id); }}
              disabled={analyzing === account.id}
              className={`px-3 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                analyzing === account.id
                  ? 'bg-white/5 text-zinc-500 cursor-not-allowed'
                  : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {analyzing === account.id ? (
                <Loader className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Page header skeleton */}
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>

        {/* Summary cards skeleton — matches the 5-column grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-zinc-800/60 rounded-2xl p-5 border border-white/5">
              <div className="w-10 h-10 rounded-xl bg-zinc-700/60 mb-3" />
              <div className="h-8 w-16 bg-zinc-700/60 rounded-lg mb-2" />
              <div className="h-3 w-12 bg-zinc-700/40 rounded" />
            </div>
          ))}
        </div>

        {/* Section header skeleton */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zinc-800/60 animate-pulse" />
          <div className="space-y-1">
            <div className="h-4 w-48 bg-zinc-800/60 rounded animate-pulse" />
            <div className="h-3 w-32 bg-zinc-800/40 rounded animate-pulse" />
          </div>
        </div>

        {/* Account cards skeleton — 8 cards in the same 3-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse bg-zinc-800/60 rounded-2xl border border-white/5 p-5">
              {/* Card header: platform icon + name + score circle */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-zinc-700/60" />
                  <div className="space-y-1.5">
                    <div className="h-4 w-28 bg-zinc-700/60 rounded" />
                    <div className="h-3 w-20 bg-zinc-700/40 rounded" />
                  </div>
                </div>
                {/* Health score circle placeholder */}
                <div className="w-16 h-16 rounded-full bg-zinc-700/60" />
              </div>
              {/* Action button placeholder */}
              <div className="h-10 w-full bg-zinc-700/40 rounded-xl mt-4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const totalAccounts = (data?.healthy?.length || 0) + (data?.warning?.length || 0) + (data?.critical?.length || 0);
  const avgScore = totalAccounts > 0 
    ? Math.round([...(data?.healthy || []), ...(data?.warning || []), ...(data?.critical || [])]
        .reduce((sum, a) => sum + a.health_score, 0) / totalAccounts)
    : 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">AI Health Analysis</h1>
        <p className="text-zinc-500 text-sm mt-1">Monitor account performance and get AI-powered insights</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-gradient-to-br from-[#22c55e]/10 to-transparent border border-[#22c55e]/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#22c55e]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-[#22c55e] font-mono">{data?.summary?.healthy_count || 0}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Healthy</p>
        </div>
        
        <div className="bg-gradient-to-br from-yellow-500/10 to-transparent border border-yellow-500/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-yellow-400 font-mono">{data?.summary?.warning_count || 0}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Warning</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-red-400 font-mono">{data?.summary?.critical_count || 0}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Critical</p>
        </div>
        
        <div className="bg-[#18181B] border border-white/10 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
              <svg className="w-5 h-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-zinc-400 font-mono">{data?.summary?.unknown_count || 0}</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Unknown</p>
        </div>

        <div className="bg-gradient-to-br from-[#6366f1]/10 to-transparent border border-[#6366f1]/20 rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#6366f1]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-bold text-[#6366f1] font-mono">{avgScore}%</p>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Avg Score</p>
        </div>
      </div>

      {/* Critical Section */}
      {data?.critical?.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-red-400">Critical - Immediate Action Required</h3>
              <p className="text-xs text-zinc-500">{data.critical.length} account{data.critical.length > 1 ? 's' : ''} need your attention</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.critical.map(account => <HealthCard key={account.id} account={account} status="critical" />)}
          </div>
        </div>
      )}

      {/* Warning Section */}
      {data?.warning?.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-yellow-400">Warning - Needs Attention</h3>
              <p className="text-xs text-zinc-500">{data.warning.length} account{data.warning.length > 1 ? 's' : ''} showing warning signs</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.warning.map(account => <HealthCard key={account.id} account={account} status="warning" />)}
          </div>
        </div>
      )}

      {/* Healthy Section */}
      {data?.healthy?.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-[#22c55e]/20 flex items-center justify-center">
              <svg className="w-4 h-4 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-[#22c55e]">Healthy - Performing Well</h3>
              <p className="text-xs text-zinc-500">{data.healthy.length} account{data.healthy.length > 1 ? 's' : ''} running smoothly</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.healthy.map(account => <HealthCard key={account.id} account={account} status="healthy" />)}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!data?.healthy?.length && !data?.warning?.length && !data?.critical?.length) && (
        <div className="text-center py-16 bg-[#09090B] rounded-2xl border border-white/10">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/5 flex items-center justify-center">
            <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">No Health Data Available</h3>
          <p className="text-zinc-500 mb-6">Sync your accounts and run AI analysis to get health insights.</p>
        </div>
      )}
    </div>
  );
};

export default HealthOverview;
